import {
  ActionIcon,
  Alert,
  Badge,
  Button,
  Card,
  Group,
  Modal,
  MultiSelect,
  NumberInput,
  Paper,
  Select,
  SimpleGrid,
  Stack,
  Tabs,
  Text,
  TextInput,
  Textarea,
  Title,
} from '@mantine/core';
import { modals } from '@mantine/modals';
import { notifications } from '@mantine/notifications';
import {
  IconArchive,
  IconArrowLeft,
  IconBrandWhatsapp,
  IconDownload,
  IconExternalLink,
  IconPencil,
  IconPhone,
  IconPlus,
  IconTrash,
  IconUnlink,
} from '@tabler/icons-react';
import { useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { api } from '../api';
import { formatMinorAmount, phoneHref, whatsappHref } from '../features/suppliers/supplier-utils';
import { requestFileDownload } from '../services/file-transfer';
import { apiHook } from '../treaty';

type ModalKind =
  | 'source'
  | 'contact'
  | 'edit-contact'
  | 'location'
  | 'edit-location'
  | 'activity'
  | 'follow-up'
  | 'complete-follow-up'
  | 'price'
  | 'void-price'
  | 'document'
  | null;

function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  const unit = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
  const value = bytes / 1024 ** unit;
  return `${value.toFixed(unit === 0 || value >= 10 ? 0 : 1)} ${units[unit]}`;
}

export function SupplierDetailPage() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const supplierQuery = apiHook.useQuery((client) => client.suppliers({ id }).get(), {
    enabled: Boolean(id),
  });
  const mastersQuery = apiHook.useQuery((client) => client['supplier-master-data'].get());
  const filesQuery = apiHook.useQuery((client) => client.files.get());
  const [modalKind, setModalKind] = useState<ModalKind>(null);
  const [selectedFollowUpId, setSelectedFollowUpId] = useState<string | null>(null);
  const [selectedRecordId, setSelectedRecordId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const supplier = supplierQuery.data;
  const masters = mastersQuery.data;

  async function refresh() {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['suppliers'] }),
      queryClient.invalidateQueries({ queryKey: ['suppliers', id] }),
      queryClient.invalidateQueries({ queryKey: ['supplier-follow-ups'] }),
      queryClient.invalidateQueries({ queryKey: ['product-catalog'] }),
    ]);
  }

  async function changeStatus(statusId: string | null) {
    if (!statusId) return;
    const response = await api.api.api.v1.suppliers({ id }).status.put({ statusId });
    if (response.error)
      notifications.show({ color: 'red', message: 'Status could not be changed.' });
    else {
      await refresh();
      notifications.show({ color: 'green', message: 'Status updated.' });
    }
  }

  async function replaceTags(tagIds: string[]) {
    const response = await api.api.api.v1.suppliers({ id }).tags.put({ tagIds });
    if (!response.error) await refresh();
  }

  async function replaceProducts(productIds: string[]) {
    const response = await api.api.api.v1.suppliers({ id }).products.put({ productIds });
    if (!response.error) await refresh();
  }

  function archive() {
    modals.openConfirmModal({
      title: supplier?.archivedAt ? 'Restore supplier?' : 'Archive supplier?',
      children: <Text size='sm'>The full history will be retained.</Text>,
      labels: { confirm: supplier?.archivedAt ? 'Restore' : 'Archive', cancel: 'Cancel' },
      confirmProps: { color: supplier?.archivedAt ? 'blue' : 'red' },
      onConfirm: async () => {
        const response = supplier?.archivedAt
          ? await api.api.api.v1.suppliers({ id }).restore.post()
          : await api.api.api.v1.suppliers({ id }).archive.post();
        if (!response.error) {
          await refresh();
          notifications.show({
            color: 'green',
            message: supplier?.archivedAt ? 'Supplier restored.' : 'Supplier archived.',
          });
        }
      },
    });
  }

  async function downloadDocument(fileId: string, name: string) {
    try {
      const ticket = await requestFileDownload(fileId);
      const anchor = document.createElement('a');
      anchor.href = ticket.url;
      anchor.download = name;
      document.body.append(anchor);
      anchor.click();
      anchor.remove();
    } catch {
      notifications.show({ color: 'red', message: 'Could not start the download.' });
    }
  }

  function unlinkDocument(documentId: string, name: string) {
    modals.openConfirmModal({
      title: 'Unlink document?',
      children: <Text size='sm'>“{name}” will remain available in Files.</Text>,
      labels: { confirm: 'Unlink', cancel: 'Cancel' },
      onConfirm: async () => {
        const response = await api.api.api.v1.suppliers({ id }).documents({ documentId }).delete();
        if (response.error) {
          notifications.show({ color: 'red', message: 'The document could not be unlinked.' });
          return;
        }
        await refresh();
        notifications.show({ color: 'green', message: 'Document unlinked.' });
      },
    });
  }

  function confirmRemove(
    title: string,
    message: string,
    action: () => Promise<{ error: unknown }>,
  ) {
    modals.openConfirmModal({
      title,
      children: <Text size='sm'>{message}</Text>,
      labels: { confirm: 'Remove', cancel: 'Cancel' },
      confirmProps: { color: 'red' },
      onConfirm: async () => {
        const response = await action();
        if (response.error) {
          notifications.show({ color: 'red', message: 'The record could not be removed.' });
          return;
        }
        await refresh();
        notifications.show({ color: 'green', message: 'Removed.' });
      },
    });
  }

  if (supplierQuery.isLoading) return <Text c='dimmed'>Loading supplier…</Text>;
  if (!supplier) return <Alert color='red'>Supplier could not be loaded.</Alert>;

  const companyChannels = supplier.channels.filter((channel) => !channel.contactId);
  const primaryContact =
    supplier.contacts.find((contact: any) => contact.isPrimary) ?? supplier.contacts[0];
  const quickChannels = primaryContact?.channels.length ? primaryContact.channels : companyChannels;
  const phone = quickChannels.find((channel: any) => channel.type === 'phone');
  const whatsapp = quickChannels.find((channel: any) => channel.type === 'whatsapp');

  return (
    <Stack gap='lg' maw={1350} mx='auto'>
      <Group justify='space-between' align='start'>
        <Group align='start'>
          <ActionIcon variant='light' mt={5} onClick={() => void navigate('/suppliers')}>
            <IconArrowLeft size={18} />
          </ActionIcon>
          <div>
            <Group gap='sm'>
              <Title order={1}>{supplier.name}</Title>
              {supplier.archivedAt && <Badge color='gray'>Archived</Badge>}
            </Group>
            <Text c='dimmed'>{supplier.website ?? 'Supplier sourcing record'}</Text>
          </div>
        </Group>
        <Group>
          {phone && (
            <Button
              component='a'
              href={phoneHref(phone.value)}
              variant='light'
              leftSection={<IconPhone size={17} />}
            >
              Call
            </Button>
          )}
          {whatsapp && (
            <Button
              component='a'
              href={whatsappHref(whatsapp.value)}
              target='_blank'
              color='green'
              variant='light'
              leftSection={<IconBrandWhatsapp size={17} />}
            >
              WhatsApp
            </Button>
          )}
          <Button
            variant='light'
            color={supplier.archivedAt ? 'blue' : 'red'}
            leftSection={<IconArchive size={17} />}
            onClick={archive}
          >
            {supplier.archivedAt ? 'Restore' : 'Archive'}
          </Button>
        </Group>
      </Group>

      <Paper withBorder radius='lg' p='md'>
        <SimpleGrid cols={{ base: 1, md: 3 }}>
          <Select
            label='Current status'
            data={(masters?.statuses ?? []).map((status) => ({
              value: status.id,
              label: status.name,
            }))}
            value={supplier.status.id}
            onChange={(value) => void changeStatus(value)}
          />
          <MultiSelect
            label='Tags'
            searchable
            data={(masters?.tags ?? []).map((tag) => ({ value: tag.id, label: tag.name }))}
            value={supplier.tags.map((row) => row.tag.id)}
            onChange={(values) => void replaceTags(values)}
          />
          <MultiSelect
            label='Products supplied'
            searchable
            data={(masters?.products ?? []).map((product) => ({
              value: product.id,
              label: [product.brand, product.name].filter(Boolean).join(' '),
            }))}
            value={supplier.products.map((row) => row.product.id)}
            onChange={(values) => void replaceProducts(values)}
          />
        </SimpleGrid>
      </Paper>

      <Tabs defaultValue='overview' keepMounted={false}>
        <Tabs.List>
          <Tabs.Tab value='overview'>Overview</Tabs.Tab>
          <Tabs.Tab value='contacts'>Contacts & locations</Tabs.Tab>
          <Tabs.Tab value='products'>Products & prices</Tabs.Tab>
          <Tabs.Tab value='activity'>Activity</Tabs.Tab>
          <Tabs.Tab value='follow-ups'>Follow-ups</Tabs.Tab>
          <Tabs.Tab value='documents'>Documents</Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value='overview' pt='lg'>
          <SimpleGrid cols={{ base: 1, md: 2 }}>
            <Section
              title='Sources'
              action={
                <Button
                  size='xs'
                  variant='light'
                  leftSection={<IconPlus size={14} />}
                  onClick={() => setModalKind('source')}
                >
                  Add source
                </Button>
              }
            >
              {supplier.sources.map((source) => (
                <Card key={source.id} withBorder padding='sm'>
                  <Group justify='space-between'>
                    <div>
                      <Text fw={600}>{source.sourceType.name}</Text>
                      <Text size='xs' c='dimmed'>
                        {dayjs(source.discoveredAt).format('DD MMM YYYY')}
                      </Text>
                    </div>
                    <Group gap={4}>
                      {source.referenceUrl && (
                        <ActionIcon
                          component='a'
                          href={source.referenceUrl}
                          target='_blank'
                          variant='light'
                          aria-label={`Open ${source.sourceType.name} source`}
                        >
                          <IconExternalLink size={16} />
                        </ActionIcon>
                      )}
                      <ActionIcon
                        variant='subtle'
                        color='red'
                        aria-label={`Remove ${source.sourceType.name} source`}
                        onClick={() =>
                          confirmRemove(
                            'Remove source?',
                            'The supplier record will remain unchanged.',
                            () =>
                              api.api.api.v1
                                .suppliers({ id })
                                .sources({ sourceId: source.id })
                                .delete(),
                          )
                        }
                      >
                        <IconTrash size={15} />
                      </ActionIcon>
                    </Group>
                  </Group>
                  {source.note && (
                    <Text size='sm' mt='xs'>
                      {source.note}
                    </Text>
                  )}
                </Card>
              ))}
            </Section>
            <Section title='Notes'>
              <Text size='sm' style={{ whiteSpace: 'pre-wrap' }}>
                {supplier.notes || 'No notes yet.'}
              </Text>
              <Text size='xs' c='dimmed' mt='md'>
                Created by {supplier.createdBy.email} · Updated{' '}
                {dayjs(supplier.updatedAt).format('DD MMM YYYY, HH:mm')}
              </Text>
            </Section>
          </SimpleGrid>
        </Tabs.Panel>

        <Tabs.Panel value='contacts' pt='lg'>
          <SimpleGrid cols={{ base: 1, md: 2 }}>
            <Section
              title='People'
              action={
                <Button
                  size='xs'
                  leftSection={<IconPlus size={14} />}
                  onClick={() => setModalKind('contact')}
                >
                  Add contact
                </Button>
              }
            >
              {!supplier.contacts.length && (
                <Text c='dimmed' size='sm'>
                  No people added.
                </Text>
              )}
              {supplier.contacts.map((contact: any) => (
                <Card key={contact.id} withBorder padding='sm'>
                  <Group justify='space-between'>
                    <div>
                      <Text fw={600}>
                        {contact.name} {contact.isPrimary && <Badge size='xs'>Primary</Badge>}
                      </Text>
                      <Text size='sm' c='dimmed'>
                        {contact.designation || 'No designation'}
                      </Text>
                    </div>
                    <Group gap={4}>
                      <ActionIcon
                        variant='subtle'
                        aria-label={`Edit ${contact.name}`}
                        onClick={() => {
                          setSelectedRecordId(contact.id);
                          setModalKind('edit-contact');
                        }}
                      >
                        <IconPencil size={15} />
                      </ActionIcon>
                      <ActionIcon
                        variant='subtle'
                        color='red'
                        aria-label={`Remove ${contact.name}`}
                        onClick={() =>
                          confirmRemove(
                            'Remove contact?',
                            'Their contact channels will also be removed.',
                            () =>
                              api.api.api.v1
                                .suppliers({ id })
                                .contacts({ contactId: contact.id })
                                .delete(),
                          )
                        }
                      >
                        <IconTrash size={15} />
                      </ActionIcon>
                    </Group>
                  </Group>
                  {contact.channels.map((channel: any) => (
                    <Text key={channel.id} size='sm'>
                      {channel.type}: {channel.value}
                    </Text>
                  ))}
                </Card>
              ))}
            </Section>
            <Section
              title='Locations'
              action={
                <Button
                  size='xs'
                  leftSection={<IconPlus size={14} />}
                  onClick={() => setModalKind('location')}
                >
                  Add location
                </Button>
              }
            >
              {!supplier.locations.length && (
                <Text c='dimmed' size='sm'>
                  No locations added.
                </Text>
              )}
              {supplier.locations.map((location: any) => (
                <Card key={location.id} withBorder padding='sm'>
                  <Group justify='space-between' wrap='nowrap'>
                    <Text fw={600}>
                      {location.label} {location.isPrimary && <Badge size='xs'>Primary</Badge>}
                    </Text>
                    <Group gap={4}>
                      <ActionIcon
                        variant='subtle'
                        aria-label={`Edit ${location.label} location`}
                        onClick={() => {
                          setSelectedRecordId(location.id);
                          setModalKind('edit-location');
                        }}
                      >
                        <IconPencil size={15} />
                      </ActionIcon>
                      <ActionIcon
                        variant='subtle'
                        color='red'
                        aria-label={`Remove ${location.label} location`}
                        onClick={() =>
                          confirmRemove('Remove location?', 'This cannot be undone.', () =>
                            api.api.api.v1
                              .suppliers({ id })
                              .locations({ locationId: location.id })
                              .delete(),
                          )
                        }
                      >
                        <IconTrash size={15} />
                      </ActionIcon>
                    </Group>
                  </Group>
                  <Text size='sm'>
                    {[
                      location.addressLine1,
                      location.city,
                      location.district,
                      location.state,
                      location.pinCode,
                    ]
                      .filter(Boolean)
                      .join(', ') || 'Address not entered'}
                  </Text>
                  {location.mapUrl && (
                    <Button
                      component='a'
                      href={location.mapUrl}
                      target='_blank'
                      variant='subtle'
                      size='compact-xs'
                    >
                      Open map
                    </Button>
                  )}
                </Card>
              ))}
            </Section>
          </SimpleGrid>
        </Tabs.Panel>

        <Tabs.Panel value='products' pt='lg'>
          <Section
            title='Supplier pricing'
            action={
              <Button
                size='xs'
                leftSection={<IconPlus size={14} />}
                onClick={() => setModalKind('price')}
              >
                Add price
              </Button>
            }
          >
            {!supplier.prices.length && (
              <Text c='dimmed' size='sm'>
                No prices recorded.
              </Text>
            )}
            {supplier.prices.map((price: any) => (
              <Card key={price.id} withBorder padding='sm' opacity={price.voidedAt ? 0.55 : 1}>
                <Group justify='space-between'>
                  <div>
                    <Text fw={600}>{price.variant.name}</Text>
                    <Text size='sm'>
                      {formatMinorAmount(price.amountMinor, price.currencyCode)} per{' '}
                      {price.priceQuantity} {price.priceUnit.symbol}
                    </Text>
                    <Text size='xs' c='dimmed'>
                      Quoted {dayjs(price.quotedAt).format('DD MMM YYYY')} · MOQ{' '}
                      {price.moqQuantity
                        ? `${price.moqQuantity} ${price.moqUnit?.symbol ?? ''}`
                        : 'not stated'}{' '}
                      · Tax {price.taxInclusion} · Freight {price.freightInclusion}
                    </Text>
                  </div>
                  {price.voidedAt ? (
                    <Badge color='red'>Voided</Badge>
                  ) : (
                    <Button
                      size='compact-xs'
                      variant='light'
                      color='red'
                      onClick={() => {
                        setSelectedRecordId(price.id);
                        setModalKind('void-price');
                      }}
                    >
                      Void
                    </Button>
                  )}
                </Group>
              </Card>
            ))}
          </Section>
        </Tabs.Panel>

        <Tabs.Panel value='activity' pt='lg'>
          <Section
            title='Timeline'
            action={
              <Button
                size='xs'
                leftSection={<IconPlus size={14} />}
                onClick={() => setModalKind('activity')}
              >
                Log activity
              </Button>
            }
          >
            {!supplier.activities.length && !supplier.statusHistory.length && (
              <Text c='dimmed' size='sm'>
                No activity yet.
              </Text>
            )}
            {[
              ...supplier.activities.map((activity: any) => ({
                kind: 'activity' as const,
                at: activity.occurredAt,
                data: activity,
              })),
              ...supplier.statusHistory.map((status: any) => ({
                kind: 'status' as const,
                at: status.createdAt,
                data: status,
              })),
            ]
              .sort((a: any, b: any) => new Date(b.at).getTime() - new Date(a.at).getTime())
              .map((item: any) =>
                item.kind === 'activity' ? (
                  <Card key={`a-${item.data.id}`} withBorder padding='sm'>
                    <Group justify='space-between'>
                      <Group>
                        <Badge color={item.data.activityType.color}>
                          {item.data.activityType.name}
                        </Badge>
                        {item.data.outcome && (
                          <Badge variant='light' color={item.data.outcome.color}>
                            {item.data.outcome.name}
                          </Badge>
                        )}
                      </Group>
                      <Text size='xs' c='dimmed'>
                        {dayjs(item.at).format('DD MMM YYYY, HH:mm')}
                      </Text>
                    </Group>
                    {item.data.notes && (
                      <Text size='sm' mt='xs'>
                        {item.data.notes}
                      </Text>
                    )}
                  </Card>
                ) : (
                  <Card key={`s-${item.data.id}`} withBorder padding='sm'>
                    <Group justify='space-between'>
                      <Text size='sm'>
                        Status changed to <b>{item.data.toStatus.name}</b>
                      </Text>
                      <Text size='xs' c='dimmed'>
                        {dayjs(item.at).format('DD MMM YYYY, HH:mm')}
                      </Text>
                    </Group>
                  </Card>
                ),
              )}
          </Section>
        </Tabs.Panel>

        <Tabs.Panel value='follow-ups' pt='lg'>
          <Section
            title='Follow-ups'
            action={
              <Button
                size='xs'
                leftSection={<IconPlus size={14} />}
                onClick={() => setModalKind('follow-up')}
              >
                Schedule
              </Button>
            }
          >
            {!supplier.followUps.length && (
              <Text c='dimmed' size='sm'>
                No follow-ups scheduled.
              </Text>
            )}
            {supplier.followUps.map((followUp: any) => (
              <Card key={followUp.id} withBorder padding='sm'>
                <Group justify='space-between'>
                  <div>
                    <Text fw={600}>{followUp.activityType.name}</Text>
                    <Text size='sm'>{followUp.instructions || 'No instructions'}</Text>
                    <Text size='xs' c='dimmed'>
                      {dayjs(followUp.dueAt).format('DD MMM YYYY, HH:mm')}
                    </Text>
                  </div>
                  {followUp.completedAt ? (
                    <Badge color='green'>Completed</Badge>
                  ) : followUp.cancelledAt ? (
                    <Badge color='gray'>Cancelled</Badge>
                  ) : (
                    <Group>
                      <Button
                        size='compact-xs'
                        onClick={() => {
                          setSelectedFollowUpId(followUp.id);
                          setModalKind('complete-follow-up');
                        }}
                      >
                        Complete
                      </Button>
                      <Button
                        size='compact-xs'
                        variant='light'
                        color='gray'
                        onClick={async () => {
                          await api.api.api.v1['supplier-follow-ups']({
                            id: followUp.id,
                          }).cancel.post();
                          await refresh();
                        }}
                      >
                        Cancel
                      </Button>
                    </Group>
                  )}
                </Group>
              </Card>
            ))}
          </Section>
        </Tabs.Panel>

        <Tabs.Panel value='documents' pt='lg'>
          <Section
            title='Documents'
            action={
              <Button
                size='xs'
                leftSection={<IconPlus size={14} />}
                onClick={() => setModalKind('document')}
              >
                Link file
              </Button>
            }
          >
            {!supplier.documents.length && (
              <Text c='dimmed' size='sm'>
                No files linked. Upload files in Files, then link them here.
              </Text>
            )}
            {supplier.documents.map((document: any) => (
              <Card key={document.id} withBorder padding='sm'>
                <Group justify='space-between' wrap='nowrap'>
                  <div>
                    <Text fw={600}>{document.label || document.file.name}</Text>
                    <Text size='xs' c='dimmed'>
                      {document.file.name} · {formatBytes(document.file.size)}
                    </Text>
                  </div>
                  <Group gap={4} wrap='nowrap'>
                    <ActionIcon
                      variant='light'
                      aria-label={`Download ${document.file.name}`}
                      onClick={() => void downloadDocument(document.file.id, document.file.name)}
                    >
                      <IconDownload size={16} />
                    </ActionIcon>
                    <ActionIcon
                      variant='light'
                      color='red'
                      aria-label={`Unlink ${document.file.name}`}
                      onClick={() => unlinkDocument(document.id, document.file.name)}
                    >
                      <IconUnlink size={16} />
                    </ActionIcon>
                  </Group>
                </Group>
              </Card>
            ))}
          </Section>
        </Tabs.Panel>
      </Tabs>

      <SupplierActionModal
        kind={modalKind}
        close={() => {
          setModalKind(null);
          setSelectedRecordId(null);
        }}
        supplier={supplier}
        masters={masters}
        files={filesQuery.data ?? []}
        followUpId={selectedFollowUpId}
        recordId={selectedRecordId}
        saving={saving}
        save={async (action) => {
          setSaving(true);
          const response = await action();
          setSaving(false);
          if (response.error) {
            notifications.show({ color: 'red', message: 'The record could not be saved.' });
            return;
          }
          setModalKind(null);
          await refresh();
          notifications.show({ color: 'green', message: 'Saved.' });
        }}
      />
    </Stack>
  );
}

function Section({
  title,
  action,
  children,
}: {
  title: string;
  action?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <Paper withBorder radius='lg' p='lg'>
      <Group justify='space-between' mb='md'>
        <Title order={3}>{title}</Title>
        {action}
      </Group>
      <Stack gap='sm'>{children}</Stack>
    </Paper>
  );
}

function SupplierActionModal({
  kind,
  close,
  supplier,
  masters,
  files,
  followUpId,
  recordId,
  saving,
  save,
}: {
  kind: ModalKind;
  close: () => void;
  supplier: NonNullable<ReturnType<typeof useSupplierShape>>;
  masters: any;
  files: Array<{ id: string; name: string }>;
  followUpId: string | null;
  recordId: string | null;
  saving: boolean;
  save: (action: () => Promise<{ error: unknown }>) => Promise<void>;
}) {
  const [name, setName] = useState('');
  const [secondary, setSecondary] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [typeId, setTypeId] = useState<string | null>(null);
  const [outcomeId, setOutcomeId] = useState<string | null>(null);
  const [contactId, setContactId] = useState<string | null>(null);
  const [when, setWhen] = useState(dayjs().format('YYYY-MM-DDTHH:mm'));
  const [notes, setNotes] = useState('');
  const [variantId, setVariantId] = useState<string | null>(null);
  const [unitId, setUnitId] = useState<string | null>(null);
  const [amount, setAmount] = useState<string | number>('');
  const [moq, setMoq] = useState<string | number>('');
  const [tax, setTax] = useState<string | null>('unknown');
  const [freight, setFreight] = useState<string | null>('unknown');
  const [fileId, setFileId] = useState<string | null>(null);
  const [locationTypeValue, setLocationTypeValue] = useState<string>('office');
  const selectedFollowUp = supplier.followUps.find((item: any) => item.id === followUpId);
  const selectedContact = supplier.contacts.find((item: any) => item.id === recordId);
  const selectedLocation = supplier.locations.find((item: any) => item.id === recordId);
  const type = masters?.activityTypes.find((item: any) => item.id === typeId);
  useEffect(() => {
    if (!kind) return;
    setName('');
    setSecondary('');
    setPhone('');
    setEmail('');
    setTypeId(kind === 'complete-follow-up' ? (selectedFollowUp?.activityTypeId ?? null) : null);
    setOutcomeId(null);
    setContactId(kind === 'complete-follow-up' ? (selectedFollowUp?.contactId ?? null) : null);
    setWhen(
      dayjs()
        .add(kind === 'follow-up' ? 1 : 0, 'day')
        .format('YYYY-MM-DDTHH:mm:ss'),
    );
    setNotes('');
    setVariantId(null);
    setUnitId(null);
    setAmount('');
    setMoq('');
    setTax('unknown');
    setFreight('unknown');
    setFileId(null);
    setLocationTypeValue('office');
    if (kind === 'edit-contact' && selectedContact) {
      setName(selectedContact.name);
      setSecondary(selectedContact.designation ?? '');
    }
    if (kind === 'edit-location' && selectedLocation) {
      setName(selectedLocation.label);
      setSecondary(selectedLocation.addressLine1 ?? '');
      setPhone(selectedLocation.city ?? '');
      setEmail(selectedLocation.state ?? '');
      setLocationTypeValue(selectedLocation.type);
    }
  }, [
    kind,
    selectedContact,
    selectedFollowUp?.activityTypeId,
    selectedFollowUp?.contactId,
    selectedLocation,
  ]);
  const title =
    kind === 'source'
      ? 'Add discovery source'
      : kind === 'contact'
        ? 'Add contact'
        : kind === 'edit-contact'
          ? 'Edit contact'
          : kind === 'location'
            ? 'Add location'
            : kind === 'edit-location'
              ? 'Edit location'
              : kind === 'activity'
                ? 'Log activity'
                : kind === 'follow-up'
                  ? 'Schedule follow-up'
                  : kind === 'complete-follow-up'
                    ? 'Complete follow-up'
                    : kind === 'price'
                      ? 'Add supplier price'
                      : kind === 'void-price'
                        ? 'Void supplier price'
                        : 'Link existing file';
  async function submit() {
    if (kind === 'source' && typeId)
      return save(() =>
        api.api.api.v1.suppliers({ id: supplier.id }).sources.post({
          sourceTypeId: typeId,
          ...(name ? { referenceText: name } : {}),
          ...(secondary ? { referenceUrl: secondary } : {}),
          ...(notes ? { note: notes } : {}),
        }),
      );
    if (kind === 'contact')
      return save(() =>
        api.api.api.v1.suppliers({ id: supplier.id }).contacts.post({
          name,
          ...(secondary ? { designation: secondary } : {}),
          isPrimary: supplier.contacts.length === 0,
          channels: [
            ...(phone ? [{ type: 'phone' as const, value: phone, isPrimary: true }] : []),
            ...(email ? [{ type: 'email' as const, value: email }] : []),
          ],
        }),
      );
    if (kind === 'edit-contact' && recordId)
      return save(() =>
        api.api.api.v1
          .suppliers({ id: supplier.id })
          .contacts({ contactId: recordId })
          .put({
            name,
            ...(secondary ? { designation: secondary } : {}),
            isPrimary: selectedContact?.isPrimary ?? false,
          }),
      );
    if (kind === 'location')
      return save(() =>
        api.api.api.v1.suppliers({ id: supplier.id }).locations.post({
          label: name || 'Primary',
          type: locationTypeValue as 'office' | 'shop' | 'factory' | 'warehouse' | 'other',
          ...(secondary ? { addressLine1: secondary } : {}),
          ...(phone ? { city: phone } : {}),
          ...(email ? { state: email } : {}),
          isPrimary: supplier.locations.length === 0,
        }),
      );
    if (kind === 'edit-location' && recordId)
      return save(() =>
        api.api.api.v1
          .suppliers({ id: supplier.id })
          .locations({ locationId: recordId })
          .put({
            label: name,
            type: locationTypeValue as 'office' | 'shop' | 'factory' | 'warehouse' | 'other',
            ...(secondary ? { addressLine1: secondary } : {}),
            ...(phone ? { city: phone } : {}),
            ...(email ? { state: email } : {}),
            isPrimary: selectedLocation?.isPrimary ?? false,
          }),
      );
    if (kind === 'activity' && typeId)
      return save(() =>
        api.api.api.v1.suppliers({ id: supplier.id }).activities.post({
          activityTypeId: typeId,
          ...(outcomeId ? { outcomeId } : {}),
          ...(contactId ? { contactId } : {}),
          occurredAt: new Date(when).toISOString(),
          ...(notes ? { notes } : {}),
        }),
      );
    if (kind === 'follow-up' && typeId)
      return save(() =>
        api.api.api.v1.suppliers({ id: supplier.id })['follow-ups'].post({
          activityTypeId: typeId,
          ...(contactId ? { contactId } : {}),
          dueAt: new Date(when).toISOString(),
          ...(notes ? { instructions: notes } : {}),
        }),
      );
    if (kind === 'complete-follow-up' && followUpId) {
      const requiresOutcome = (type?.outcomes ?? []).some((item: any) => item.active);
      if (requiresOutcome && !outcomeId) {
        notifications.show({ color: 'red', message: 'Choose the activity outcome.' });
        return;
      }
      return save(() =>
        api.api.api.v1['supplier-follow-ups']({ id: followUpId }).complete.post({
          ...(outcomeId ? { outcomeId } : {}),
          occurredAt: new Date(when).toISOString(),
          ...(notes ? { notes } : {}),
        }),
      );
    }
    if (kind === 'price' && variantId && unitId)
      return save(() =>
        api.api.api.v1.suppliers({ id: supplier.id }).prices.post({
          variantId,
          amountMinor: Math.round(Number(amount) * 100),
          priceUnitId: unitId,
          priceQuantity: 1,
          ...(Number(moq) > 0 ? { moqQuantity: Number(moq), moqUnitId: unitId } : {}),
          taxInclusion: tax as 'included' | 'excluded' | 'unknown',
          freightInclusion: freight as 'included' | 'excluded' | 'unknown',
          quotedAt: new Date(when).toISOString(),
        }),
      );
    if (kind === 'void-price' && recordId && notes.trim())
      return save(() =>
        api.api.api.v1['supplier-prices']({ id: recordId }).void.post({ reason: notes.trim() }),
      );
    if (kind === 'document' && fileId)
      return save(() =>
        api.api.api.v1
          .suppliers({ id: supplier.id })
          .documents.post({ fileId, ...(name ? { label: name } : {}) }),
      );
    notifications.show({ color: 'red', message: 'Complete the required fields.' });
  }
  return (
    <Modal opened={kind !== null} onClose={close} title={title} size='lg'>
      <Stack>
        {kind === 'source' && (
          <>
            <Select
              required
              label='Source type'
              data={(masters?.sourceTypes ?? []).map((item: any) => ({
                value: item.id,
                label: item.name,
              }))}
              value={typeId}
              onChange={setTypeId}
            />
            <TextInput
              label='Reference'
              placeholder='Listing name, referral, or other reference'
              value={name}
              onChange={(e) => setName(e.currentTarget.value)}
            />
            <TextInput
              label='Source URL'
              placeholder='https://…'
              value={secondary}
              onChange={(e) => setSecondary(e.currentTarget.value)}
            />
            <Textarea
              label='Note'
              value={notes}
              onChange={(e) => setNotes(e.currentTarget.value)}
            />
          </>
        )}
        {(kind === 'contact' || kind === 'edit-contact') && (
          <>
            <TextInput
              required
              label='Name'
              value={name}
              onChange={(e) => setName(e.currentTarget.value)}
            />
            <TextInput
              label='Designation'
              value={secondary}
              onChange={(e) => setSecondary(e.currentTarget.value)}
            />
            {kind === 'contact' && (
              <SimpleGrid cols={2}>
                <TextInput
                  label='Phone'
                  value={phone}
                  onChange={(e) => setPhone(e.currentTarget.value)}
                />
                <TextInput
                  label='Email'
                  value={email}
                  onChange={(e) => setEmail(e.currentTarget.value)}
                />
              </SimpleGrid>
            )}
          </>
        )}
        {(kind === 'location' || kind === 'edit-location') && (
          <>
            <TextInput
              required
              label='Label'
              placeholder='Factory, office, shop…'
              value={name}
              onChange={(e) => setName(e.currentTarget.value)}
            />
            <Select
              label='Location type'
              data={[
                { value: 'office', label: 'Office' },
                { value: 'shop', label: 'Shop' },
                { value: 'factory', label: 'Factory' },
                { value: 'warehouse', label: 'Warehouse' },
                { value: 'other', label: 'Other' },
              ]}
              value={locationTypeValue}
              onChange={(value) => setLocationTypeValue(value ?? 'office')}
            />
            <TextInput
              label='Address'
              value={secondary}
              onChange={(e) => setSecondary(e.currentTarget.value)}
            />
            <SimpleGrid cols={2}>
              <TextInput
                label='City'
                value={phone}
                onChange={(e) => setPhone(e.currentTarget.value)}
              />
              <TextInput
                label='State'
                value={email}
                onChange={(e) => setEmail(e.currentTarget.value)}
              />
            </SimpleGrid>
          </>
        )}
        {(kind === 'activity' || kind === 'follow-up') && (
          <>
            <Select
              required
              label='Type'
              data={(masters?.activityTypes ?? []).map((item: any) => ({
                value: item.id,
                label: item.name,
              }))}
              value={typeId}
              onChange={(value) => {
                setTypeId(value);
                setOutcomeId(null);
              }}
            />
            {kind === 'activity' && (
              <Select
                clearable
                label='Outcome'
                data={(type?.outcomes ?? [])
                  .filter((item: any) => item.active)
                  .map((item: any) => ({ value: item.id, label: item.name }))}
                value={outcomeId}
                onChange={setOutcomeId}
              />
            )}
            <Select
              clearable
              label='Contact'
              data={supplier.contacts.map((item: any) => ({ value: item.id, label: item.name }))}
              value={contactId}
              onChange={setContactId}
            />
            <TextInput
              type='datetime-local'
              step={1}
              label={kind === 'activity' ? 'Occurred at' : 'Due at'}
              value={when}
              onChange={(e) => setWhen(e.currentTarget.value)}
            />
            <Textarea
              label={kind === 'activity' ? 'Notes' : 'Instructions'}
              value={notes}
              onChange={(e) => setNotes(e.currentTarget.value)}
            />
          </>
        )}
        {kind === 'complete-follow-up' && (
          <>
            <TextInput label='Activity type' value={type?.name ?? ''} disabled />
            {(type?.outcomes ?? []).some((item: any) => item.active) && (
              <Select
                required
                label='Outcome'
                data={(type?.outcomes ?? [])
                  .filter((item: any) => item.active)
                  .map((item: any) => ({ value: item.id, label: item.name }))}
                value={outcomeId}
                onChange={setOutcomeId}
              />
            )}
            <TextInput
              type='datetime-local'
              step={1}
              label='Completed at'
              value={when}
              onChange={(e) => setWhen(e.currentTarget.value)}
            />
            <Textarea
              label='Completion notes'
              value={notes}
              onChange={(e) => setNotes(e.currentTarget.value)}
            />
          </>
        )}
        {kind === 'price' && (
          <>
            <Select
              required
              searchable
              label='Variant'
              data={(masters?.variants ?? []).map((item: any) => ({
                value: item.id,
                label: item.name,
              }))}
              value={variantId}
              onChange={(value) => {
                setVariantId(value);
                const variant = masters?.variants.find((item: any) => item.id === value);
                setUnitId(variant?.defaultUnitId ?? null);
              }}
            />
            <SimpleGrid cols={2}>
              <NumberInput
                required
                label='Price'
                prefix='₹ '
                min={0}
                decimalScale={2}
                value={amount}
                onChange={setAmount}
              />
              <Select
                required
                label='Per unit'
                data={(masters?.units ?? []).map((item: any) => ({
                  value: item.id,
                  label: `${item.name} (${item.symbol})`,
                }))}
                value={unitId}
                onChange={setUnitId}
              />
            </SimpleGrid>
            <SimpleGrid cols={3}>
              <NumberInput label='MOQ' min={0} value={moq} onChange={setMoq} />
              <Select
                label='Tax'
                data={['unknown', 'included', 'excluded']}
                value={tax}
                onChange={setTax}
              />
              <Select
                label='Freight'
                data={['unknown', 'included', 'excluded']}
                value={freight}
                onChange={setFreight}
              />
            </SimpleGrid>
            <TextInput
              type='datetime-local'
              step={1}
              label='Quoted at'
              value={when}
              onChange={(e) => setWhen(e.currentTarget.value)}
            />
          </>
        )}
        {kind === 'void-price' && (
          <Textarea
            required
            label='Reason'
            description='The observation remains in price history with this reason.'
            value={notes}
            onChange={(e) => setNotes(e.currentTarget.value)}
          />
        )}
        {kind === 'document' && (
          <>
            <Select
              required
              searchable
              label='File'
              data={files.map((file) => ({ value: file.id, label: file.name }))}
              value={fileId}
              onChange={setFileId}
            />
            <TextInput
              label='Display label'
              value={name}
              onChange={(e) => setName(e.currentTarget.value)}
            />
            <Text size='xs' c='dimmed'>
              Upload new files from the Files module, then link them here.
            </Text>
          </>
        )}
        <Group justify='flex-end'>
          <Button variant='default' onClick={close}>
            Cancel
          </Button>
          <Button loading={saving} onClick={() => void submit()}>
            Save
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

function useSupplierShape() {
  return apiHook.useQuery((client) => client.suppliers({ id: '' }).get()).data;
}
